import React from "react";
import styled from "styled-components";

const MessageGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isSent ? 'flex-end' : 'flex-start'};
  margin-bottom: 12px;
  max-width: 85%;
  align-self: ${props => props.isSent ? 'flex-end' : 'flex-start'};
`;

const BubbleContainer = styled.div`
  padding: 8px 12px;
  border-radius: 18px;
  position: relative;
  max-width: 100%;
  word-wrap: break-word;
  margin-bottom: 2px;
  
  ${props => props.isSent ? `
    background-color: #1a73e8;
    color: white;
    border-bottom-right-radius: ${props.consecutive ? '18px' : '4px'};
  ` : `
    background-color: #383a3f;
    color: #eaeaea;
    border-bottom-left-radius: ${props.consecutive ? '18px' : '4px'};
  `}
`;

const SenderName = styled.div`
  font-size: 12px;
  color: ${props => props.isSent ? '#c5d7f7' : '#b0b3b8'};
  margin-bottom: 2px;
  font-weight: 500;
`;

const TimeIndicator = styled.span`
  font-size: 10px;
  color: ${props => props.isSent ? 'rgba(255, 255, 255, 0.7)' : '#999'};
  margin-top: 2px;
  align-self: ${props => props.isSent ? 'flex-end' : 'flex-start'};
`;

const DateDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0;
  width: 100%;
  
  &:before, &:after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #3e4042;
  }
  
  span {
    padding: 0 10px;
    color: #b0b3b8;
    font-size: 12px;
  }
`;

/**
 * ChatBubble component - Reusable component for displaying chat messages
 * 
 * @param {Object} props
 * @param {string} props.message - The message text
 * @param {string} props.sender - The sender's name
 * @param {string} props.time - The message timestamp
 * @param {boolean} props.isSent - Whether the message was sent by the current user
 * @param {boolean} props.consecutive - Whether this message is part of a consecutive group
 * @param {boolean} props.showSender - Whether to show the sender name
 * @param {boolean} props.showTime - Whether to show the time
 * @param {boolean} props.isEdited - Whether the message has been edited
 * @param {string} props.messageType - The type of the message (e.g., 'text', 'system')
 */
export const ChatBubble = ({
  message,
  sender,
  time,
  isSent = false,
  consecutive = false,
  showSender = true,
  showTime = true,
  isEdited = false,
  messageType = 'text'  // Add message type prop
}) => {
  // Determine if this is a system message
  const isSystem = messageType === 'system' || sender === 'system';
  
  return (
    <>
      {showSender && !isSent && !isSystem && <SenderName isSent={isSent}>{sender}</SenderName>}
      <BubbleContainer 
        isSent={isSent} 
        consecutive={consecutive} 
        isSystem={isSystem}
        style={isSystem ? { 
          backgroundColor: 'rgba(150, 150, 150, 0.2)', 
          color: '#aaa',
          alignSelf: 'center',
          fontStyle: 'italic',
          maxWidth: '80%'
        } : {}}
      >
        {message}
        {isEdited && <small style={{ opacity: 0.7, marginLeft: '4px' }}> (edited)</small>}
      </BubbleContainer>
      {showTime && <TimeIndicator isSent={isSent}>{time}</TimeIndicator>}
    </>
  );
};

/**
 * ChatMessageGroup component - Groups related messages together
 */
export const ChatMessageGroup = ({ children, isSent = false }) => {
  return (
    <MessageGroup isSent={isSent}>
      {children}
    </MessageGroup>
  );
};

/**
 * ChatDateDivider component - Shows a date separator in the chat
 */
export const ChatDateDivider = ({ date }) => {
  return (
    <DateDivider>
      <span>{date}</span>
    </DateDivider>
  );
};

export default {
  Bubble: ChatBubble,
  MessageGroup: ChatMessageGroup,
  DateDivider: ChatDateDivider
};
